<template>
    <template v-if="$route.name === 'salary.management'">
  <div class="col-12 col-xl-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <!-- Actions -->
    <div class="row g-2 align-items-center mb-3">
      <div class="col-12 d-grid gap-2 d-md-flex justify-content-md-end">
        <button class="btn btn-outline-secondary btn-sm" @click="onPrint" :disabled="isLoading">
          <i class="fas fa-print me-2"></i>{{ $t("salary_management.print") }}
        </button>
        <button class="btn btn-outline-success btn-sm" @click="onExportExcel" :disabled="isLoading">
          <i class="fas fa-file-excel me-2"></i>{{ $t("salary_management.export_excel") }}
        </button>
        <button class="btn btn-outline-danger btn-sm" @click="onExportPdf" :disabled="isLoading || exportingPdf">
          <i class="fas fa-file-pdf me-2"></i>{{ $t("salary_management.export_pdf") }}
        </button>
        <router-link
          class="btn btn-success btn-sm"
          :to="{ name: 'salaries.createPayment' }"
          v-can="'salary_management.create'"
        >
          <i class="fas fa-money-bill-wave me-2"></i>
          {{ $t("salary_management.add_payment") }}
        </router-link>
      </div>
    </div>

    <!-- Card -->
    <div
      class="card mb-3 mb-xxl-5 me-xxl-4"
      style="margin-top: 2.5rem; overflow: visible"
    >
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div
          class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100"
        >
          <h6
            class="text-white text-capitalize ps-3 pe-3"
            style="font-size: 1rem"
          >
            {{ $t("salary_management.title") }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="isLoading"
            :message="$t('salary_management.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile cards -->
            <div class="d-block d-xl-none">
              <div
                v-if="safeReceipts.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-receipt fa-2x mb-2"></i>
                <p class="mb-0">{{ $t("salary_management.no_receipts") }}</p>
              </div>

              <div
                v-for="r in safeReceipts"
                :key="'mobile-receipt-' + r.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-3">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <h6 class="mb-1 text-sm">
                        {{ $t("salary_management.receipt_no") }}:
                        {{ r.receipt_no }}
                      </h6>
                      <p class="text-xs text-muted mb-1">
                        <i class="fas fa-user me-1"></i> {{ r.model_name }}
                      </p>
                      <span
                        class="badge badge-sm"
                        :class="getTypeBadgeClass(r.type)"
                      >
                        {{ getTypeName(r.type) }}
                      </span>
                    </div>
                    <div class="text-end">
                      <div class="fw-bold text-sm">
                        {{ formatDisplay(r.value) }} {{ $t("common.currency") }}
                      </div>
                      <div class="text-xs text-muted">
                        <i class="fas fa-calendar-alt me-1"></i
                        >{{ r.payment_date }}
                      </div>
                    </div>
                  </div>

                  <div class="mt-2 text-xs text-muted">
                    {{ r.description || "--" }}
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button
                      class="btn btn-link text-secondary p-0 mx-2"
                      @click="openEditPage(r)"
                      :title="$t('common.edit')"
                      v-can="'salary_management.update'"
                    >
                      <i class="fas fa-edit fa-sm"></i>
                    </button>
                    <button
                      class="btn btn-link text-danger p-0 mx-2"
                      @click="openDeleteDialog(r)"
                      :title="$t('common.delete')"
                      v-can="'salary_management.delete'"
                    >
                      <i class="fas fa-trash fa-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop table -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("salary_management.receipt_no") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("salary_management.employee") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("salary_management.type") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("salary_management.value") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("salary_management.payment_date") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("salary_management.description") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4"
                  >
                    {{ $t("common.actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="safeReceipts.length === 0">
                  <td colspan="7" class="text-center py-4 text-muted">
                    <i class="fas fa-receipt fa-2x mb-2"></i>
                    <p class="mb-0">
                      {{ $t("salary_management.no_receipts") }}
                    </p>
                  </td>
                </tr>

                <tr v-for="r in safeReceipts" :key="r.id">
                  <td>
                    <span class="text-sm">{{ r.receipt_no }}</span>
                  </td>
                  <td>
                    <span class="text-sm">{{ r.model_name }}</span>
                  </td>
                  <td>
                    <span
                      class="badge badge-sm"
                      :class="getTypeBadgeClass(r.type)"
                    >
                      {{ getTypeName(r.type) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-sm"
                      >{{ formatDisplay(r.value) }}
                      {{ $t("common.currency") }}</span
                    >
                  </td>
                  <td>
                    <span class="text-sm">{{ r.payment_date }}</span>
                  </td>
                  <td>
                    <span class="text-sm text-muted">{{
                      r.description || "--"
                    }}</span>
                  </td>
                  <td class="align-middle text-center pe-4">
                    <div class="d-inline-flex">
                      <button
                        class="btn btn-link text-secondary p-1 mx-1"
                        @click="openEditPage(r)"
                        :title="$t('common.edit')"
                        v-can="'salary_management.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </button>
                      <button
                        class="btn btn-link text-danger p-1 mx-1"
                        @click="openDeleteDialog(r)"
                        :title="$t('common.delete')"
                        v-can="'salary_management.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <SalaryReceiptEditDialog ref="editDialog" @save="handleEditSave" />
    <DeleteDialog
      ref="deleteDialog"
      :entity-name="$t('salary_management.receipt')"
      :entity-label="$t('salary_management.receipt')"
      @delete="handleDeleteReceipt"
    />
  </div>
  </template>
    <router-view></router-view>

</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import SalaryReceiptEditDialog from "@/components/features/salary/SalaryReceiptEditDialog.vue";
import DeleteDialog from "@/components/common/DeleteDialog.vue";
import { useTableExport, formatPrintDate } from "@/composables/useTableExport";

const { t } = useI18n();
const store = useStore();
const toast = useToast();
const router = useRouter();
const { printTable, exportExcel, exportPdf } = useTableExport();
const exportingPdf = ref(false);


const editDialog = ref(null);
const deleteDialog = ref(null);

const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const cleaned = String(v).replace(/[^\d.-]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatDisplay = (v) => {
  const n = toNumber(v);
  return Number.isInteger(n)
    ? String(n)
    : String(n.toFixed(2)).replace(/\.?0+$/, "");
};

const storeReceipts = computed(() => {
  const receipts = store.getters["salaryManagement/receipts"];
  return Array.isArray(receipts) ? receipts : [];
});
const isLoading = computed(() => !!store.getters["salaryManagement/isLoading"]);

const safeReceipts = computed(() => {
  if (!Array.isArray(storeReceipts.value)) return [];
  return storeReceipts.value
    .filter(
      (r) => r && typeof r === "object" && r.id !== undefined && r.id !== null
    )
    .map((r) => ({
      id: r.id ?? 0,
      receipt_no: r.receipt_no || "N/A",
      value: toNumber(r.value ?? r.amount ?? r.salary ?? 0),
      type: r.type || "unknown",
      description: r.description || "",
      payment_date: r.payment_date || "N/A",
      model_name: r.model_name || r.employee_name || r.user_name || "Unknown",
      model_type: r.model_type || "unknown",
      created_at: r.created_at || "N/A",
    }));
});

const fetchReceipts = async () => {
  try {
    // Defensive: the dedicated rewards/deductions list pages set a type
    // filter on this same shared store module. Reset it here too so this
    // combined view always shows every type regardless of navigation path.
    await store.dispatch("salaryManagement/resetFilters");
    await store.dispatch("salaryManagement/fetchReceipts");
  } catch (error) {
    toast.error(t("salary_management.fetch_error"));
  }
};

const openEditPage = (receipt) => router.push({ name: 'salaries.editReceipt', params: { id: receipt.id } });

const handleEditSave = async (payload) => {
  try {
    await store.dispatch("salaryManagement/updateReceipt", {
      id: payload.id,
      data: {
        type: payload.type,
        value: toNumber(payload.value),
        payment_date: payload.payment_date,
        description: payload.description,
      },
    });
    toast.success(t("common.saved"));
    await fetchReceipts();
  } catch (e) {
    toast.error(e?.response?.data?.message || t("common.error"));
  }
};

const openDeleteDialog = (receipt) => {
  if (deleteDialog.value && receipt?.id) {
    deleteDialog.value.openDialog(receipt.id, receipt.receipt_no || "Receipt");
  }
};

const handleDeleteReceipt = async (receiptId) => {
  try {
    await store.dispatch("salaryManagement/deleteReceipt", receiptId);
    toast.success(t("salary_management.receipt_deleted"));
    await fetchReceipts();
  } catch (error) {
    toast.error(
      error?.response?.data?.message || t("salary_management.delete_error")
    );
  }
};
// map type -> badge class
const getTypeBadgeClass = (type) => {
  const map = {
    salary: 'bg-success',
    on_account: 'bg-info',
    reward: 'bg-warning',
    deduction: 'bg-danger',
    unknown: 'bg-secondary'
  };
  return map[type] || 'bg-secondary';
};

// localized label for type
const getTypeName = (type) => {
  const map = {
    salary: t('salary_management.salary'),
    on_account: t('salary_management.on_account'),
    reward: t('salary_management.reward'),
    deduction: t('salary_management.deduction'),
    unknown: t('common.unknown')
  };
  return map[type] || type;
};

const receiptColumns = () => [
  { key: "receipt_no", label: t("salary_management.receipt_no") },
  { key: "model_name", label: t("salary_management.employee") },
  { key: "type", label: t("salary_management.type"), format: (r) => getTypeName(r.type) },
  { key: "value", label: t("salary_management.value"), format: (r) => formatDisplay(r.value) },
  { key: "payment_date", label: t("salary_management.payment_date") },
  { key: "description", label: t("salary_management.description") },
];

const onPrint = () => {
  printTable({
    title: t("salary_management.title"),
    subtitleLines: [`${t("salary_management.print_date")}: ${formatPrintDate()}`],
    columns: receiptColumns(),
    rows: safeReceipts.value,
  });
};

const onExportExcel = () => {
  exportExcel({
    title: t("salary_management.title"),
    filename: "salary-receipts",
    columns: receiptColumns(),
    rows: safeReceipts.value,
  });
};

const onExportPdf = async () => {
  exportingPdf.value = true;
  try {
    await exportPdf({
      title: t("salary_management.title"),
      filename: "salary-receipts",
      subtitleLines: [`${t("salary_management.print_date")}: ${formatPrintDate()}`],
      columns: receiptColumns(),
      rows: safeReceipts.value,
    });
  } catch (e) {
    toast.error(t("common.error"));
  } finally {
    exportingPdf.value = false;
  }
};

onMounted(() => {
  fetchReceipts();
});
</script>

<style scoped>
.borderless-input {
  border: none;
  border-bottom: 1px solid #e9ecef;
  border-radius: 0;
}
.borderless-input:focus {
  box-shadow: none;
  border-bottom-color: #6c757d;
}
.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}
.bg-success-subtle {
  background-color: rgba(25, 135, 84, 0.1);
}
.bg-secondary-subtle {
  background-color: rgba(108, 117, 125, 0.1);
}
</style>
